import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import DataTable from 'components/shared/DataTable';
import Checkbox from 'components/shared/form/Checkbox';
import FormHeader from 'components/shared/form/FormHeader';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { useProfile } from 'context/profile';
import { useAdminForm } from 'hooks/datasource';
import { PERMISSIONS } from 'models/Profile';
import {
  adminPortalSystemQueryKeys,
  AdminRetrievalField,
  AdminScanField,
  showAdminPortalSystem,
  updateAdminPortalSystem,
} from 'services/api/admin/portalSystems';
import { portalSystemQueryKeys } from 'services/api/portalSystems';
import { colors } from 'styles/theme/colors';

import { primaryIdLabel } from './PortalSystems';

// Admin → Portal Systems → Edit (OLIO-3074): the selection side of a portal's
// collected-field configuration. The universe of collectable fields is code, so the
// tables list every field and lock the non-negotiables; the form only carries what
// the admin can move.
type FormValues = {
  retrievalFields: string[];
  scanFields: string[];
  sweepTableFields: string[];
  primaryId: string;
};

type PrimaryIdOption = { value: string; label: string };

function toggled(names: string[], name: string, on: boolean) {
  return on ? [...names, name] : names.filter((n) => n !== name);
}

export default function EditPortalSystem() {
  const { id } = useParams();
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const canEdit = profile.has(PERMISSIONS.adminClientEdit);

  const form = useAdminForm({
    id,
    show: showAdminPortalSystem,
    create: async () => {},
    update: async (updateId: string, payload: FormValues) => {
      const updated = await updateAdminPortalSystem(updateId, payload);
      queryClient.invalidateQueries({ queryKey: portalSystemQueryKeys.base });
      return updated;
    },
    include: [] as const,
    queryKey: adminPortalSystemQueryKeys,
    toFormValues: (data): FormValues => ({
      retrievalFields: (data?.retrievalFields ?? []).filter((f) => f.enabled && !f.required).map((f) => f.name),
      scanFields: (data?.scanFields ?? []).filter((f) => f.enabled && !f.required).map((f) => f.name),
      sweepTableFields: (data?.scanFields ?? []).filter((f) => f.showOnSweepsTable).map((f) => f.name),
      primaryId: data?.primaryId ?? 'url',
    }),
    toPayload: (values) => values,
    onUpdateSuccess: { toast: 'Portal system successfully updated!', navigate: '/admin/portal-systems' },
    onError: { toast: 'Something went wrong. Please try again.' },
  });

  const system = form.data;
  const values = form.values;

  const isCollected = (field: AdminRetrievalField | AdminScanField, selected: string[]) =>
    field.required || selected.includes(field.name);

  // A scan field the admin turns off cannot stay a table column; the api rejects the
  // combination, so drop the column in the same change.
  const setScanField = (name: string, on: boolean) =>
    form.setValues({
      scanFields: toggled(values.scanFields, name, on),
      sweepTableFields: on ? values.sweepTableFields : values.sweepTableFields.filter((n) => n !== name),
    });

  const retrievalHelper = createColumnHelper<AdminRetrievalField>();
  const retrievalColumns = useMemo(
    () => [
      retrievalHelper.accessor('label', {
        header: 'Field',
        enableSorting: false,
        cell: (data) => <FieldName field={data.row.original} />,
      }),
      retrievalHelper.accessor('type', {
        header: 'Type',
        enableSorting: false,
        cell: (data) => <TypeTag>{data.getValue()}</TypeTag>,
      }),
      retrievalHelper.display({
        id: 'collected',
        header: 'Collected',
        size: 100,
        cell: (data) => {
          const field = data.row.original;
          return (
            <Checkbox
              size={20}
              checked={isCollected(field, values.retrievalFields)}
              disabled={!canEdit || field.required}
              onChange={(on) => form.setValue('retrievalFields', toggled(values.retrievalFields, field.name, on))}
              dataCyLabel={`retrieval-field-${field.name}`}
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.retrievalFields, canEdit]
  );

  const scanHelper = createColumnHelper<AdminScanField>();
  const scanColumns = useMemo(
    () => [
      scanHelper.accessor('label', {
        header: 'Field',
        enableSorting: false,
        cell: (data) => <FieldName field={data.row.original} />,
      }),
      scanHelper.accessor('type', {
        header: 'Type',
        enableSorting: false,
        cell: (data) => <TypeTag>{data.getValue()}</TypeTag>,
      }),
      scanHelper.display({
        id: 'collected',
        header: 'Collected',
        size: 100,
        cell: (data) => {
          const field = data.row.original;
          return (
            <Checkbox
              size={20}
              checked={isCollected(field, values.scanFields)}
              disabled={!canEdit || field.required}
              onChange={(on) => setScanField(field.name, on)}
              dataCyLabel={`scan-field-${field.name}`}
            />
          );
        },
      }),
      scanHelper.display({
        id: 'sweepTable',
        header: 'Sweeps Table Column',
        size: 160,
        cell: (data) => {
          const field = data.row.original;
          return (
            <Checkbox
              size={20}
              checked={values.sweepTableFields.includes(field.name)}
              disabled={!canEdit || !isCollected(field, values.scanFields)}
              onChange={(on) => form.setValue('sweepTableFields', toggled(values.sweepTableFields, field.name, on))}
              dataCyLabel={`sweep-table-${field.name}`}
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.scanFields, values.sweepTableFields, canEdit]
  );

  const primaryIdOptions: PrimaryIdOption[] = useMemo(
    () => (system ? system.primaryIdOptions.map((value) => ({ value, label: primaryIdLabel(system, value) })) : []),
    [system]
  );

  if (form.loading || !system) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title='Edit Portal System' subtitle={system.displayName} />

      <HelpText>Collected fields apply to every client using this portal.</HelpText>

      <SectionHeader>Collected on Retrieval</SectionHeader>

      <TableWrapper data-testid='retrieval-fields-table'>
        <DataTable columns={retrievalColumns} data={system.retrievalFields} />
      </TableWrapper>

      <SectionHeader>Collected on Scans</SectionHeader>

      <TableWrapper data-testid='scan-fields-table'>
        <DataTable columns={scanColumns} data={system.scanFields} />
      </TableWrapper>

      <SectionHeader>Primary ID</SectionHeader>

      <InputGroup title='Identifies a retrieval on the portal'>
        <SelectWrapper>
          <Select
            inputId='primary-id'
            options={primaryIdOptions}
            value={primaryIdOptions.find((option) => option.value === values.primaryId) ?? null}
            getOptionValue={(option: PrimaryIdOption | null) => option!.value}
            getOptionLabel={(option: PrimaryIdOption | null) => option!.label}
            onChange={(option: PrimaryIdOption | null) => form.setValue('primaryId', option?.value ?? 'url')}
            isDisabled={!canEdit}
          />
        </SelectWrapper>
      </InputGroup>

      <ButtonGroup style={{ justifyContent: 'flex-end' }}>
        <Button variant='ghost' onClick={form.goBack}>
          Cancel
        </Button>
        {canEdit && (
          <Button
            data-testid='portal-systems-save'
            onClick={form.submit}
            disabled={!form.dirty || form.submitting}
            loading={form.submitting}>
            Update Portal System
          </Button>
        )}
      </ButtonGroup>
    </AdminFormPage>
  );
}

function FieldName({ field }: { field: AdminRetrievalField | AdminScanField }) {
  return (
    <FieldNameCell>
      <BoldText>{field.label}</BoldText>
      {field.required && <RequiredTag>required</RequiredTag>}
    </FieldNameCell>
  );
}

const HelpText = styled.p`
  color: ${colors.black50};
  font-size: 14px;
  margin: 0 0 20px;
`;

const TableWrapper = styled.div`
  margin-bottom: 16px;
`;

const FieldNameCell = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const BoldText = styled.span`
  font-weight: 500;
`;

const TypeTag = styled.span`
  background: ${colors.black10};
  border-radius: 4px;
  color: ${colors.black50};
  font-size: 11px;
  padding: 2px 6px;
`;

const RequiredTag = styled(TypeTag)`
  color: ${colors.black75};
`;

const SelectWrapper = styled.div`
  max-width: 320px;
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
